import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '@/app/store/store';
import { physicalStoreBuild } from '../../config/physicalStore.build';
import { toStoreLocationWidgetVm } from '../viewModels/physicalStore.vm';
import type { StoreLocationWidgetVm } from '../viewModels/physicalStore.vm';

const SESSION_KEY = 'bs_slw_shown';

// Module-level: survives virtualizer remounts within the same PLP page visit.
// undefined = not yet fetched | null = fetched, no data | object = fetched with data
let _pageLoadData: StoreLocationWidgetVm | null | undefined = undefined;
// true while the widget is being shown in the current PLP visit (prevents sessionStorage
// from suppressing remounts caused by the window virtualizer scrolling the row in/out)
let _showingNow = false;

/**
 * Call this when ProductListingPage unmounts (SPA navigation away from PLP).
 * Resets module-level state so the next PLP visit re-checks sessionStorage correctly.
 */
export function clearStoreLocationWidgetSession(): void {
  _showingNow = false;
  _pageLoadData = undefined;
}

export interface UseStoreLocationWidgetResult {
  data: StoreLocationWidgetVm | null;
  isLoading: boolean;
  suppressed: boolean;
}

export function useStoreLocationWidget(pincode?: string): UseStoreLocationWidgetResult {
  const dispatch = useDispatch<AppDispatch>();
  const { service } = useMemo(() => physicalStoreBuild(dispatch), [dispatch]);

  // Suppress only when sessionStorage says "shown" AND we are NOT currently in the middle
  // of showing the widget in this PLP visit (_showingNow guards against virtualizer remounts).
  const [suppressed, setSuppressed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    if (_showingNow) return false;
    return Boolean(sessionStorage.getItem(SESSION_KEY));
  });

  // On virtualizer remount _showingNow is true so _pageLoadData is already set; initialise
  // state from module-level cache to avoid a flash of empty/loading on scroll-back.
  const [data, setData] = useState<StoreLocationWidgetVm | null>(
    _showingNow ? (_pageLoadData ?? null) : null
  );
  const [isLoading, setIsLoading] = useState<boolean>(!suppressed && !_showingNow);

  // Track previous pincode to detect user-triggered changes after initial mount.
  const prevPincodeRef = useRef<string | undefined>(undefined);
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      prevPincodeRef.current = pincode;
      return;
    }
    if (prevPincodeRef.current === pincode) return;
    prevPincodeRef.current = pincode;

    // Pincode changed after mount — invalidate RTK Query cache so the next fetch
    // hits the server with the new pincode cookie, then reset local state.
    service.invalidateStoreLocationWidget();
    _pageLoadData = undefined;
    _showingNow = false;
    if (typeof window !== 'undefined') sessionStorage.removeItem(SESSION_KEY);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- pincode change resets all widget state; must be synchronous before re-fetch
    setSuppressed(false);
     
    setData(null);
     
    setIsLoading(true);
  }, [pincode, service]);

  useEffect(() => {
    if (suppressed) return;
    // Already fetched in this PLP visit — useState was seeded from _pageLoadData, nothing to do.
    if (_pageLoadData !== undefined) return;

    let mounted = true;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initiates async fetch; all subsequent state updates happen inside promise callbacks
    setIsLoading(true);
    service
      .getStoreLocationWidget()
      .then((result) => {
        if (!mounted) return;
        const vm = result ? toStoreLocationWidgetVm(result) : null;
        _pageLoadData = vm;
        if (vm) {
          _showingNow = true;
          sessionStorage.setItem(SESSION_KEY, '1');
        } else {
          _pageLoadData = null;
        }
        setData(vm);
      })
      .catch((err) => {
        console.error('[useStoreLocationWidget] Failed to load:', err);
        _pageLoadData = null;
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
    // pincode is included so this effect re-runs when the user changes their pincode.
    // The pincode-change effect above resets _pageLoadData = undefined first (effects run
    // in declaration order), so the guard `if (_pageLoadData !== undefined) return` won't
    // short-circuit and a fresh fetch is triggered.
  }, [service, suppressed, pincode]);

  return { data, isLoading, suppressed };
}
